import useHelpers from 'hooks/helpers';
import usePayload from 'hooks/useFundDetailsRequestPayload';
import { useI18n } from 'next-localization';
import { useSelector } from 'react-redux';
import { getSelectedShareClass } from '@/store/fundDetails';
import {
  useFetchKeyInformationTabDataQuery,
  useFetchDisclaimerDataQuery,
  useLazyFetchKeyInfoCefFieldsQuery,
} from '@/store/fundDetails/services/fundDetailsApi';
import dictionaryItems from '@/constants/keyInfoTableFields';
import { standardFormat, UTCFormatNumeric } from '@/constants/dateFormats';
import FundDetailsTabRow from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabRow';
import FundDetailsTabSkeleton from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabSkeleton';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import { useEffect, useMemo, useState } from 'react';
import ManualDisclaimer from 'components/Generic/ManualDisclaimer';
import { getFundHeroType } from '@/store/fundDetails';

const FundDetailsKeyInformationTab = () => {
  const { localizeDate, formatCurrency, formatNumber, getErrorDictionary, isExperienceEditor } =
    useHelpers();
  const selectedShareClass = useSelector(getSelectedShareClass);
  const fundHeroType = useSelector(getFundHeroType);
  const payload = usePayload();
  const { t } = useI18n();
  const [cefFields, setCefFields] = useState({});

  const { data, error, isLoading } = useFetchKeyInformationTabDataQuery(payload, {
    skip: isExperienceEditor,
  });
  const disclaimerData = useFetchDisclaimerDataQuery(payload, {
    skip: isExperienceEditor,
  });
  const [fetchData, { isFetching: isCefFieldsLoading }] = useLazyFetchKeyInfoCefFieldsQuery();

  const mergedKeyInformationData = useMemo(() => {
    const mappedCEFFields = {};
    for (const [key, value] of Object.entries(cefFields)) {
      mappedCEFFields[key] = value.value;
    }

    const mergedData = {
      ...data?.content,
      manualFund: { ...mappedCEFFields },
    };
    return mergedData;
  }, [data, cefFields]);

  const footnote = useMemo(() => {
    return data?.content?.staticText?.fundStatisticsFootnote;
  }, [data]);

  const currencySign = () => {
    const dictionaryKey = t('fundDetailsKeyInfoCurrencySymbol');
    return dictionaryKey && dictionaryKey !== 'fundDetailsKeyInfoCurrencySymbol'
      ? dictionaryKey
      : '';
  };

  const formatItemValue = (item: string | any, key: string) => {
    if (!item || (dictionaryItems[key].type === 'object' && !item.value))
      return t('notAvailableShortMessage');

    const [price] = selectedShareClass?.prices || [{}];

    const priceCurrencyCode = price?.priceCurrencyCode || 'GBP';

    if (key === 'm2mVsAmortised') {
      return formatCurrency(item, priceCurrencyCode, 'standard', 6);
    }

    if (key === 'iopvValue') {
      return formatNumber(item, 'decimal', 4);
    }

    if (item.date) {
      return `${currencySign()} ${formatCurrency(item.value, priceCurrencyCode, 'compact')} ${t(
        'asOf'
      )} ${localizeDate(item.date)}`;
    }

    switch (dictionaryItems[key].type) {
      case 'date':
        return localizeDate(item, standardFormat);
      case 'UTCDate':
        return localizeDate(item, UTCFormatNumeric);
      case 'number':
        return `${currencySign()} ${formatCurrency(item, priceCurrencyCode, 'compact')}`;
      case 'percentage':
        return formatNumber(item, dictionaryItems[key].type);
      case 'numberWithStandardNotation':
        return formatCurrency(item, priceCurrencyCode, 'standard');
      case 'numberWithoutDecimals':
        return Math.trunc(item);
      case 'object':
        return parseFloat(item.value).toFixed(2) + ' (' + localizeDate(item.date) + ')';
      default:
        return item;
    }
  };
  const haveFootnote = (key: string) => {
    return key === 'wal' || key === 'wam' ? '[1]' : '';
  };

  const ComponentContent = () => {
    return (
      <>
        <table aria-label="Fund details codes tab table" className="w-100">
          <thead hidden>
            <tr>
              <th scope="col">Fund key information name</th>
              <th scope="col">Fund key information value</th>
            </tr>
          </thead>
          <tbody className="d-flex flex-column">
            {Object.entries(mergedKeyInformationData).map(([, data]) => (
              <>
                {Object.entries(data).map(
                  ([key, value]) =>
                    key !== 'fundStatisticsFootnote' && (
                      <tr
                        key={key}
                        className="d-flex"
                        style={{
                          order: key === 'sdrLabel' || key === 'sdrClassification' ? -1 : undefined,
                          borderBottom: '1px solid rgba(0, 0, 0, 0.3)',
                        }}
                      >
                        <td className="h6 w-50 py-4">
                          {t(dictionaryItems[key].name) + haveFootnote(key)}
                        </td>
                        <td className="body-default w-50 py-4">{formatItemValue(value, key)}</td>
                      </tr>
                    )
                )}
              </>
            ))}
          </tbody>
        </table>
        {footnote && (
          <div className="container mt-4 py-2 px-2 background-digital-light-grey">
            <p className="footnote">{'[1] ' + footnote}</p>
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    const fetchCefFields = async () => {
      try {
        const { data } = await fetchData({
          fundId: payload.fund,
          language: payload.language,
        });
        setCefFields(data);
      } catch (e) {
        console.log(e);
      }
    };
    // check if current fund is CEF fund
    if (fundHeroType === 1 || fundHeroType === 2) {
      fetchCefFields();
    }
  }, [fundHeroType]);

  return (
    <FundDetailsTabRow fullWidth={true} className="mt-4">
      {isLoading || isCefFieldsLoading ? (
        <FundDetailsTabSkeleton />
      ) : error ? (
        <AlertMessageBase type="error" title={t(getErrorDictionary(error))} />
      ) : mergedKeyInformationData ? (
        <ComponentContent />
      ) : (
        <div className="body-default">{t('noResultsMessage')}</div>
      )}

      {disclaimerData.data && disclaimerData.data.length !== 0 && (
        <ManualDisclaimer disclaimerData={disclaimerData.data} className="mt-4" />
      )}
    </FundDetailsTabRow>
  );
};

export default FundDetailsKeyInformationTab;
